import { useCallback, useState } from "react";
import { Box, useTheme } from "@mui/material";

import type { UUID } from "@thalamos/aspire-common";

import { api } from "../../../api.js";
import {
  Banner,
  BannerList,
  Button,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";

/** API functions that can be injected for testing */
export interface RetractRedoDialogApi {
  retractRedo: (
    formId: UUID,
    body: { reason: string },
  ) => Promise<{ status: number }>;
}

const defaultApi: RetractRedoDialogApi = {
  retractRedo: (formId, body) => api.forms.retractRedo(formId, body),
};

/**
 * Shown to the user who raised a redo request so they can withdraw it (with a
 * reason) before the author accepts it. Retracting reverts the form to completed
 * and restores the links that were severed when the request was made.
 */
export function RetractRedoDialog({
  formId,
  onClose,
  onSuccess,
  apiOverride,
}: {
  formId: UUID;
  onClose: () => void;
  onSuccess: () => void;
  apiOverride?: RetractRedoDialogApi;
}) {
  const apiClient = apiOverride ?? defaultApi;
  const theme = useTheme();

  const [reason, setReason] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const retract = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const result = await apiClient.retractRedo(formId, {
        reason: reason.trim(),
      });
      if (result.status === 204) {
        renderSuccessToast({ message: "Redo request retracted" });
        onSuccess();
      } else {
        throw new Error("Unknown error");
      }
    } catch (e) {
      renderErrorToast({ message: (e as Error).message });
      setIsSubmitting(false);
    }
  }, [apiClient, formId, reason, onSuccess]);

  return (
    <PopupDialog open={true} onClose={onClose}>
      <PopupDialogTitle
        titleText="Retract Redo Request"
        closeDialog={onClose}
      />
      <Box sx={{ mb: 2 }}>
        <Banner
          body={[
            "Withdraw your request for this form to be redone. The form returns to its completed state and any links removed when you requested the redo are restored.",
          ]}
          bannerType={BannerList.WARNING}
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <TextField
          name="retractReason"
          label="Please provide a reason for retracting"
          onChange={(e: string) => {
            setReason(e);
          }}
          value={reason}
          useFullWidth={true}
          multiline
          rows="4"
        />
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          marginTop: theme.spacing(3),
        }}
      >
        <Button label={"Close"} onClick={onClose} variant="outlined" />
        <Button
          color="primary"
          label={"Retract request"}
          disabled={isSubmitting || !reason.trim()}
          onClick={() => {
            void retract();
          }}
        />
      </Box>
    </PopupDialog>
  );
}
